/**
 * TypeScript's view of the workspace, shared by every Monaco editor.
 *
 * Monaco's TypeScript worker only knows two kinds of file: the models editors
 * have open, and the "extra libs" registered on `typescriptDefaults`. Anything
 * an open file imports has to be an extra lib or the import has no types.
 *
 * Two rules make that work, and both live here:
 *
 * - **One file, one URI: `file:///<workspace path>`.** The compiler options set
 *   `baseUrl: "file:///"` and map `@/*` to `*`, so `@/utils/helper` is looked up
 *   as `file:///utils/helper.ts` and `bear/*` as `file:///node_modules/bear/*`.
 *   Registering a file anywhere else makes it invisible to an import.
 * - **One owner.** `typescriptDefaults` is a singleton per Monaco instance while
 *   editors are per pane, so the workspace is registered once here rather than
 *   once per editor. Monaco hands the second registration of an identical file a
 *   no-op dispose handle, which means N panes racing on one registry ends with
 *   whichever pane tears down first deleting libs the others still need.
 *
 * Files are read through the playground HAL, so mounted packages
 * (`node_modules/bear/…`) come along with project files: a walk of the project
 * doc alone is blind across a mount. Text files keep a live subscription, so an
 * edit in another pane (or by an agent) retypechecks here immediately; mounted
 * content is immutable and is read once.
 *
 * The file an editor is showing is registered too. Its model and its extra lib
 * share a URI, which the worker resolves in the model's favour: the program
 * dedupes by file name and reads text from the model first, so the editor still
 * typechecks what is on screen, not a snapshot of it.
 *
 * Staying current is this module's job, not its callers': the registry watches
 * the project tree itself, so an editor calls {@link registerWorkspaceTypes}
 * once and is done. Nothing has to notice a file was created and pass word
 * along.
 *
 * Registration is a fetch per file across every mount, so it takes seconds on a
 * cold open. Type-aware diagnostics are therefore owned here too: until the
 * workspace is registered, every `bear/…` import resolves to nothing and the
 * editor would red-underline code that is fine. They are on exactly when the
 * registry is synced ({@link applyWorkspaceDiagnostics}); syntax validation
 * needs no types and stays on throughout.
 */

import type { Monaco } from "@monaco-editor/react";
import type { HostPlayground } from "playground/hal/index";
import { invariant } from "ts-invariant";

const TS_EXTS = [".ts", ".tsx", ".js", ".jsx"];

/** One registered file: what it was registered from, and how to unregister. */
interface RegisteredFile {
  /** Backing-store URI at registration. A different one means a new file. */
  uri: string;
  dispose(): void;
}

interface Registry {
  /** The workspace these libs came from. A different one is a new project. */
  playground: HostPlayground;
  files: Map<string, RegisteredFile>;
  /** The workspace has been registered at least once: types can be trusted. */
  synced: boolean;
  /** Tail of the sync chain: one sync runs at a time. */
  tail: Promise<void>;
  /** A sync is already waiting, so a burst of writes collapses into it. */
  queued: boolean;
  unwatch: () => void;
}

const registries = new WeakMap<Monaco, Registry>();

/**
 * Register the workspace with Monaco and keep it registered: every TypeScript
 * source the playground can see becomes an extra lib, and the registry watches
 * the project tree so files added, renamed or deleted later follow.
 *
 * Idempotent: every editor calls it on mount, and they share one registry.
 * Resolves once the workspace is in sync.
 */
export function registerWorkspaceTypes(
  monaco: Monaco,
  playground: HostPlayground,
): Promise<void> {
  const registry = registryFor(monaco, playground);
  return schedule(monaco, registry).then(() => {
    registry.synced = true;
    applyWorkspaceDiagnostics(monaco);
  });
}

/**
 * Point Monaco's diagnostics at what it currently knows: type-aware checks are
 * enabled once the workspace is registered and suppressed before that. Call it
 * from `beforeMount` too, so the first model is created into the right state
 * rather than validated against an empty workspace.
 */
export function applyWorkspaceDiagnostics(monaco: Monaco): void {
  const ready = registries.get(monaco)?.synced === true;
  const options = {
    noSyntaxValidation: false,
    noSemanticValidation: !ready,
    noSuggestionDiagnostics: !ready,
  };
  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(options);
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(options);
}

function registryFor(monaco: Monaco, playground: HostPlayground): Registry {
  const existing = registries.get(monaco);
  if (existing?.playground === playground) return existing;
  // A different playground is a different project: its libs are now wrong.
  existing?.unwatch();
  for (const file of existing?.files.values() ?? []) file.dispose();

  const registry: Registry = {
    playground,
    files: new Map(),
    synced: false,
    tail: Promise.resolve(),
    queued: false,
    unwatch: () => {},
  };
  registry.unwatch = playground.watchTree(() => {
    void schedule(monaco, registry).catch((error: unknown) => {
      console.error("Failed to register workspace types", error);
    });
  });
  registries.set(monaco, registry);
  // Nothing is registered yet, and on a project switch what was is now wrong.
  applyWorkspaceDiagnostics(monaco);
  return registry;
}

/** Queue a sync, unless one is already queued and would cover this change. */
function schedule(monaco: Monaco, registry: Registry): Promise<void> {
  if (registry.queued) return registry.tail;
  registry.queued = true;
  // A failed sync must not poison the chain, but the caller still sees it.
  registry.tail = registry.tail
    .catch(() => {})
    .then(() => {
      registry.queued = false;
      return sync(monaco, registry.playground, registry);
    });
  return registry.tail;
}

async function sync(
  monaco: Monaco,
  playground: HostPlayground,
  registry: Registry,
): Promise<void> {
  const entries = await playground.listFiles(undefined, { recursive: true });
  const sources = new Map(
    entries
      .filter(
        (entry) =>
          !entry.isDirectory && TS_EXTS.some((ext) => entry.path.endsWith(ext)),
      )
      .map((entry) => [entry.path, entry.inode.uri] as const),
  );

  for (const [path, registered] of registry.files) {
    if (sources.get(path) === registered.uri) continue;
    registered.dispose();
    registry.files.delete(path);
  }

  await Promise.all(
    [...sources]
      .filter(([path]) => !registry.files.has(path))
      .map(async ([path, uri]) => {
        const registered = await registerFile(monaco, playground, path);
        registry.files.set(path, { uri, ...registered });
      }),
  );
}

async function registerFile(
  monaco: Monaco,
  playground: HostPlayground,
  path: string,
): Promise<Omit<RegisteredFile, "uri">> {
  const defaults = monaco.languages.typescript.typescriptDefaults;
  const libUri = `file:///${path}`;
  const file = await playground.open(path);

  if (file.kind === "text") {
    let lib = defaults.addExtraLib(await file.read(), libUri);
    const unsubscribe = file.onChange(() => {
      void file.read().then((text) => {
        lib.dispose();
        lib = defaults.addExtraLib(text, libUri);
      });
    });
    return {
      dispose: () => {
        unsubscribe();
        lib.dispose();
        void file.close();
      },
    };
  }

  // Mounted package source: immutable bytes, so one read is the whole job.
  invariant(file.kind === "blob", `Not a source file: ${path}`);
  try {
    const bytes = await file.read();
    const lib = defaults.addExtraLib(new TextDecoder().decode(bytes), libUri);
    return { dispose: () => lib.dispose() };
  } finally {
    await file.close();
  }
}
